import { Check } from "lucide-react";
import Container from "@/components/ui/Container";
import Button from "@/components/ui/Button";
import Reveal from "@/components/common/Reveal";
import { cn } from "@/lib/cn";
import { usePlans } from "@/hooks/usePlans";

export default function Pricing() {
  // Live catalog — the same numbers the backend enforces, so the landing page
  // can't quote a run allowance the product doesn't actually give.
  const { plans } = usePlans();

  return (
    <section id="pricing" className="relative scroll-mt-20 py-20 sm:py-28">
      <Container>
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400">
            Pricing
          </span>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
            Pay for runs, not seats
          </h2>
          <p className="mt-4 text-slate-600 dark:text-slate-300">
            Every plan shows the real cost of each AI run, so you always know what you&apos;re spending.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {plans.map((plan, i) => (
            <Reveal key={plan.id} delay={i * 0.07}>
              <div className={cn(
                "card relative flex h-full flex-col p-7",
                plan.featured && "border-brand-300 shadow-glow ring-1 ring-brand-400/40 dark:border-brand-500/40"
              )}>
                {plan.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-600 px-3 py-1 text-xs font-semibold text-white">
                    Most popular
                  </span>
                )}

                <div className="mb-1 text-[0.6875rem] font-bold uppercase tracking-widest text-slate-400">
                  {plan.label}
                </div>

                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {plan.name}
                </h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {plan.blurb}
                </p>

                <div className="mt-4 flex items-end gap-1">
                  <span className="font-display text-3xl font-bold text-slate-900 dark:text-white">
                    {plan.priceDisplay}
                  </span>
                  {plan.period && (
                    <span className="mb-1 text-sm text-slate-500 dark:text-slate-400">
                      {plan.period}
                    </span>
                  )}
                </div>

                <dl className="mt-5 rounded-xl border border-slate-200 bg-slate-50/60 px-3.5 py-2.5 text-sm dark:border-white/10 dark:bg-white/5">
                  {plan.limitLines.map((l) => (
                    <div key={l.key} className="flex items-baseline justify-between gap-2 py-0.5">
                      <dt className="text-slate-500 dark:text-slate-400">{l.label}</dt>
                      <dd className="font-semibold text-slate-800 dark:text-slate-100">{l.value}</dd>
                    </div>
                  ))}
                </dl>

                <ul className="mt-4 flex-1 space-y-2.5">
                  {plan.perks.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <Check size={16} className="mt-0.5 shrink-0 text-brand-500" />
                      <span className="text-slate-600 dark:text-slate-300">{f}</span>
                    </li>
                  ))}
                </ul>

                {plan.price_inr === null || plan.price_inr === undefined ? (
                  // Enterprise → open the contact/support widget, not signup.
                  <Button
                    type="button"
                    variant="primary"
                    className="mt-6 w-full"
                    onClick={() => window.dispatchEvent(new CustomEvent("aiqa:contact"))}
                  >
                    {plan.cta}
                  </Button>
                ) : (
                  <Button
                    // Paid packs carry ?plan=<id> so signup routes to the payment
                    // step after verification; the free trial has no plan param.
                    to={Number(plan.price_inr) > 0 ? `/signup?plan=${plan.id}` : "/signup"}
                    variant="primary"
                    className="mt-6 w-full"
                  >
                    {plan.cta}
                  </Button>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10 text-center text-sm text-slate-500 dark:text-slate-400">
          All plans include bug reports, screenshots, and full run history.
          Free Trial needs no credit card.
        </Reveal>
      </Container>
    </section>
  );
}
